import { getGuid } from 'owa-guid';
import { composeStore } from 'owa-mail-compose-store';
import { addPopoutV2 } from 'owa-popout-v2';
import WindowFeaturesOptions from 'owa-popout-v2/lib/store/schema/WindowFeaturesOptions';
import {
    cloneSxSStore,
    getOrCreateSxSStoreData,
    onComposeStateChange,
    onCustomizationChange,
    onSxSReadingPaneChange,
    SxSCustomization,
    SXSV2_CONTAINER_ID,
    lazyRemoveSxSViewFromDOM,
    SxSReadingPaneMode,
} from 'owa-sxs-store';
import { getStore, TabViewState } from 'owa-tab-store';
import { TabType } from 'owa-tab-store/lib/store/schema/TabViewState';
import addTab from 'owa-tab-store/lib/utils/addTab';
import { getUserMailboxInfo, ClientItemId } from 'owa-client-ids';
import { getGroupMailboxInfo } from 'owa-group-common';
import {
    lazyInitializeItemReadingPaneInSxS,
    SxSReadingPaneInitializeMethod,
} from 'owa-attachment-preview-sxs-actions';
import ensureSxSTabHandler from './SxSTabHandler';
import { isFeatureEnabled } from 'owa-feature-flags';
import * as trace from 'owa-trace';
import closeTab from 'owa-tab-store/lib/actions/closeTab';

const SXSID_PREFIX = 'SXSID_';
const DEFAULT_POPOUT_WIDTH = 1920;

function hideRightHandSide(sxsId: string) {
    onComposeStateChange(null, sxsId);
    onSxSReadingPaneChange(null, sxsId);
}

function cloneSxS(sxsId: string): string {
    const clonedSxSId = SXSID_PREFIX + getGuid();
    cloneSxSStore(sxsId, clonedSxSId);
    return clonedSxSId;
}

function convertToItemId(parentItemId: ClientItemId): ClientItemId | null {
    const itemId = parentItemId?.Id;
    if (itemId) {
        let groupId: string | null = null;
        if (parentItemId.mailboxInfo && parentItemId.mailboxInfo.type === 'GroupMailbox') {
            const emailAddressParts = parentItemId.mailboxInfo.mailboxSmtpAddress.split('@');
            const groupDomain = emailAddressParts[1];
            const groupAlias = emailAddressParts[0];
            groupId = groupDomain && groupAlias ? `${groupAlias}@${groupDomain}` : null;
        }

        const mailboxInfo = groupId ? getGroupMailboxInfo(groupId) : getUserMailboxInfo();
        return { Id: itemId, mailboxInfo: mailboxInfo };
    }
    return null;
}

/**
 * Popout SxS to a new window
 * @param sxsId The sxsId that is used to display sxs view
 * @param parentWindow The original window it pops out from
 */
export default async function popOutSxS(sxsId: string, parentWindow: Window): Promise<boolean> {
    let originalTab: TabViewState | null = null;
    for (const tab of getStore().tabs) {
        if (tab.sxsId === sxsId) {
            originalTab = tab;
            break;
        }
    }
    if (originalTab === null) {
        trace.errorThatWillCauseAlert(
            'No tab found that is associated with sxsId. Something went very wrong.'
        );
        return false;
    }

    let newSxSId = sxsId;
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    const composeViewState = sxsStore.composeId && composeStore.viewStates.get(sxsStore.composeId);
    const popOutFromComposeTab: boolean = originalTab?.type === TabType.Compose;
    const readingPaneState = sxsStore.readingPaneState;
    const options: Partial<WindowFeaturesOptions> = {};
    const sxsWidth =
        parentWindow?.document?.getElementById(SXSV2_CONTAINER_ID)?.getBoundingClientRect()
            ?.width ?? DEFAULT_POPOUT_WIDTH;

    // Pop out the same SxS if the original SxS displays compose and it is in a Compose Tab
    if (composeViewState && popOutFromComposeTab) {
        options.width = sxsWidth;
        lazyRemoveSxSViewFromDOM.importAndExecute(parentWindow);
        closeTab(originalTab, true /*forceClose*/);
        ensureSxSTabHandler();
    } else if (readingPaneState && isFeatureEnabled('rp-persistedReadingPaneTabs')) {
        // Pop out the same SxS if original SxS displays an item reading pane
        if (readingPaneState.mode === SxSReadingPaneMode.ItemReadingPane) {
            options.width = sxsWidth;
            newSxSId = cloneSxS(sxsId);
            ensureSxSTabHandler();
        }
        // Convert to item reading pane and pop out SxS if original SxS displays a conversation reading pane
        else {
            newSxSId = cloneSxS(sxsId);
            // convert itemId for rendering item reading pane
            const clientItemId = convertToItemId(sxsStore.editState?.itemId);
            if (clientItemId) {
                options.width = sxsWidth;
                const initializeItemReadingPaneInSxS = await lazyInitializeItemReadingPaneInSxS.import();
                initializeItemReadingPaneInSxS(
                    {
                        method: SxSReadingPaneInitializeMethod.LoadItemReadingPane,
                        itemId: clientItemId,
                    } /* readingPaneStrategy */,
                    newSxSId /* sxsId */
                );
                ensureSxSTabHandler();
            } else {
                hideRightHandSide(newSxSId);
            }
        }
    }
    // Pop out SxS that only shows the preview pane if the original SxS does not have a reading pane
    else {
        newSxSId = cloneSxS(sxsId);
        hideRightHandSide(newSxSId);
    }

    onCustomizationChange(SxSCustomization.PopOut, newSxSId);

    const tabId = addTab(TabType.SxS, false /* isShown */, false /* makeActive */, newSxSId);
    addPopoutV2(
        'mail',
        'sxs',
        {
            deeplinkData: () => {},
            projectionTabId: tabId,
            projectionParentWindow: parentWindow,
        },
        options
    );

    return newSxSId !== sxsId;
}
